import numeral from 'numeral';
import { toast } from 'react-toastify';
import { generatePath } from 'react-router-dom';
import parsePhoneNumber from 'libphonenumber-js';
import { FormikValues, FormikConfig } from 'formik';
import { MouseEventHandler, ChangeEventHandler } from 'react';
import scrollIntoView, {
  StandardBehaviorOptions as ScrollOptions,
} from 'scroll-into-view-if-needed';

import { useAlert } from 'hooks';
import { URL } from 'api/constants';
import { Cart } from 'store/api/cart';
import { opsApiInstance } from 'api/instance';
import { EventCheckout } from 'store/api/event';
import { CartItem } from 'store/api/cart/types';
import { Chargeback } from 'store/api/accounting';
import { convertHeicToJpeg } from 'utils/heic-to-jpeg';
import { FiservCaseDetail } from 'store/api/fiserv/types';

import {
  managerRoles,
  EmptyPlaceholder,
  genericCardholderNames,
} from '../config';
import {
  SelectValue,
  SelectOption,
  InputFieldProps,
  MultiSelectValue,
  SelectFieldProps,
  SingleSelectValue,
} from '../components';
import {
  User,
  Company,
  Carrier,
  Nullable,
  FileType,
  UploadFile,
  BaseEntity,
  Commission,
  PaymentMethod,
  FormatCaseInfo,
  BackOfficeRoles,
  EventOrderStatus,
  PaymentMethodType,
} from '../types';

import { replaceAll } from './string';
import { CarrierTitle, CHARGEBACK_STATUS_MAP } from './../types/titles';

export const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const calculateOffset = (page: number, size: number): number => {
  return (page - 1) * size;
};

export const joinStrings = (
  values: Nullable<string>[],
  separator: string,
  placeholder: string | null = EmptyPlaceholder
): string => {
  const data = values.filter((value) => value && value?.length > 0) as string[];
  return data.length === 0 ? (placeholder ?? '') : data.join(separator);
};

export const getFullName = (
  firstName: Nullable<string>,
  lastName: Nullable<string>
) => {
  return joinStrings([firstName, lastName], ' ');
};

export const fullNameComponents = (fullName?: string) => {
  if (!fullName) {
    return {
      lastName: undefined,
      firstName: undefined,
    };
  }
  const components = fullName.split(' ');
  return {
    firstName: components[0],
    lastName: components.length > 1 && components[components.length - 1],
  };
};

export const textValue = (
  value?: number | string | null,
  template?: string | null,
  placeholder: string = EmptyPlaceholder
): string | number => {
  if (!value || String(value).length === 0) {
    return placeholder;
  }
  if (value && template) {
    return template.replace('_value_', String(value));
  }
  return value;
};

export const getByPath = (object?: any, path?: string): any | undefined => {
  if (!object || !path) {
    return undefined;
  }
  return path.split('.').reduce((r, k) => r?.[k], object);
};

export const stringToBoolean = (value: string): boolean => value === 'true';

export const getValuesByKeyFromTreeNodes = <T, K extends keyof T>(
  data: T[],
  key: K,
  childKey: K
): unknown[] => {
  return data.reduce((acc, item) => {
    const children = item[childKey] as unknown as T[];
    const childrenKeys = children
      ? getValuesByKeyFromTreeNodes(children, key, childKey)
      : [];

    return [...acc, item[key], ...childrenKeys];
  }, [] as unknown[]);
};

export const addIndexesToTreeNodes = <T, K extends keyof T>(
  data: T[],
  childKey: K
): T[] => {
  return data.map((item, index) => {
    const children = item[childKey] as unknown as T[];

    if (children) {
      return {
        ...item,
        $$index: index,
        [childKey]: addIndexesToTreeNodes(children, childKey),
      };
    }

    return { ...item, $$index: index };
  });
};

export const formatPhone = (
  phone?: string,
  placeholder: string = EmptyPlaceholder,
  national: boolean = true
) => {
  if (!phone) {
    return placeholder;
  }
  const phoneNumber = parsePhoneNumber(phone);
  if (!phoneNumber) {
    return phone;
  }
  return national
    ? phoneNumber.formatNational()
    : phoneNumber.formatInternational();
};

export const chunkArray = <T>(items: T[], split: number) =>
  items.reduce((chunks: T[][], item: T, index) => {
    const chunk = Math.floor(index / split);
    chunks[chunk] = ([] as T[]).concat(chunks[chunk] || [], item);
    return chunks;
  }, []);

export const splitPhones = (phones: string[]) => {
  const mobilePhones: string[] = [];
  const homePhones: string[] = [];
  phones.forEach((phone, index) => {
    if (index % 2 === 0) {
      mobilePhones.push(phone);
    } else {
      homePhones.push(phone);
    }
  });
  return { homePhones, mobilePhones };
};

export const sortSelectOptions = (options: SelectOption[]) => {
  return options.sort((a, b) => {
    const nameA = a.label.toLowerCase() ?? '';
    const nameB = b.label.toLowerCase() ?? '';
    if (nameA === nameB) {
      return 0;
    }
    return nameA > nameB ? 1 : -1;
  });
};

type PluralOptions = {
  onlyWord?: boolean;
  rightCount?: boolean;
  pluralSuffix?: string;
  singularSuffix?: string;
};

export const getPlural = (
  word: string,
  count: number,
  options?: PluralOptions
) => {
  const singularSuffix = options?.singularSuffix ?? '';
  const pluralSuffix = options?.pluralSuffix ?? 's';
  const rightCount = options?.rightCount ?? false;
  const resultWord = `${word}${count === 1 ? singularSuffix : pluralSuffix}`;
  if (options?.onlyWord) {
    return resultWord;
  }
  return rightCount ? `${count} ${resultWord}` : `${resultWord} - ${count}`;
};

export const hasSelection = () => {
  const selection = window.getSelection();
  return (
    selection && selection.toString().length > 0 && selection.type === 'Range'
  );
};

export const handleSelectionOnClick = <T = HTMLDivElement>(
  onClick?: MouseEventHandler<T>
) => {
  if (!onClick) {
    return undefined;
  }
  const handle: MouseEventHandler<T> = (e) => {
    if (hasSelection()) {
      return;
    }
    onClick(e);
  };
  return handle;
};

export const handleSelectionOnChange = <T = HTMLInputElement>(
  onChange?: ChangeEventHandler<T>
) => {
  if (!onChange) {
    return undefined;
  }
  const handle: ChangeEventHandler<T> = (e) => {
    if (hasSelection()) {
      return;
    }
    onChange(e);
  };
  return handle;
};

export const isFilterApplied = (value: unknown): boolean => {
  if (value instanceof Set) {
    return value.size > 0;
  } else if (Array.isArray(value) || typeof value === 'string') {
    return value.length > 0;
  } else if (typeof value === 'number') {
    return value !== 0;
  } else if (typeof value === 'object' && value) {
    return Object.values(value).some(Boolean);
  }

  return Boolean(value);
};

export const areSomeFiltersApplied = <T extends Record<string, any>>(
  filters: T
): boolean => Object.values(filters).some(isFilterApplied);

export const getRandomColor = <T extends object>(
  name: string,
  anyEnum: T
): T[keyof T] => {
  let sum = 0;
  const colors = Object.values(anyEnum);

  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }

  const index = sum % colors.length;
  return colors[index] as unknown as T[keyof T];
};

export const getUserInitials = (name: string) => {
  return name
    .split(/\s+/)
    .filter((n) => n.length > 0)
    .map((n) => n[0].toUpperCase())
    .join('');
};

export const formatNumber = (value?: any, format: string = '0,0') => {
  return numeral(value).format(format);
};

export const formatPrice = (value?: any, format: string = '$0,0.00') => {
  return formatNumber(value, format);
};

export const isSingleSelectValue = (
  value: unknown
): value is SingleSelectValue =>
  typeof value === 'object' &&
  value !== null &&
  'value' in value &&
  'label' in value;

export const isMultiSelectValue = (
  value: unknown
): value is MultiSelectValue => {
  if (Array.isArray(value)) {
    if (value.length > 0) {
      return 'label' in value[0] && 'value' in value[0];
    }

    return true;
  }

  return false;
};

export const isSelectValue = (value: unknown): value is SelectValue =>
  isMultiSelectValue(value) || isSingleSelectValue(value);

export const getSelectValue = ({
  value,
  options,
  onlyValue,
  isCreatable,
}: {
  value: string | string[] | SelectValue;
} & Pick<
  SelectFieldProps,
  'options' | 'onlyValue' | 'isCreatable'
>): SelectValue => {
  if (!onlyValue && isSelectValue(value)) {
    return value;
  }
  const getOption = (search?: any): SingleSelectValue => {
    let option = options?.find(
      (item) => !Array.isArray(item) && (item as any).value === search
    );
    if (!option && isCreatable && search) {
      option = { label: search, value: search };
    }
    if (isSingleSelectValue(option)) {
      return option;
    }
  };
  if (Array.isArray(value)) {
    return value?.map((item) => getOption(item)) as MultiSelectValue;
  }
  return getOption(value) ?? null;
};

export const basicScrollOptions: ScrollOptions = {
  block: 'nearest',
  inline: 'nearest',
  behavior: 'smooth',
  scrollMode: 'if-needed',
};

export const scrollToId = (
  id: string,
  options: ScrollOptions = basicScrollOptions
) => {
  const element = document.getElementById(id);
  if (element) {
    scrollIntoView(element, options);
  }
};

export const scrollToClassName = (
  name: string,
  options: ScrollOptions = basicScrollOptions
) => {
  const elements = document.getElementsByClassName(name);
  const element = elements.length > 0 ? elements.item(0) : null;
  if (element) {
    scrollIntoView(element, options);
  }
};

export const scrollToErrorMessage = (
  options: ScrollOptions = basicScrollOptions
) => {
  const elements = document.getElementsByClassName('error-message');
  const element = elements.length > 0 ? elements.item(0) : null;
  if (element) {
    scrollIntoView(element.parentElement ?? element, options);
  }
};

export const arrayMove = (
  arr: Array<any>,
  oldIndex: number,
  newIndex: number
) => {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
};

export const waitForElementToExist = (selector: string) => {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });
  });
};

export const ordinalNumber = (value: number) => {
  let suffix = '';
  switch (value) {
    case 1:
      suffix = 'st';
      break;
    case 2:
      suffix = 'nd';
      break;
    case 3:
      suffix = 'rd';
      break;
    default:
      suffix = 'th';
      break;
  }
  return `${value}${suffix}`;
};

/**
 * Where OPS stores an upload. Omitted = the public `uploads/` prefix used by
 * product images, avatars, logos and receipts (unchanged). Employee W-9 and
 * contract files go to a private prefix and are opened through a short-lived
 * signed URL (see getEmployeeDocumentUrl).
 */
export type UploadCategory = 'employee-document';

export const uploadMultimedia = async <T extends Record<string, any>>(
  file: File,
  url: string,
  category?: UploadCategory,
  // e.g. { employeeId } so OPS binds an employee document to that employee
  scope?: Record<string, string | number>
): Promise<T> => {
  const formData = new FormData();
  // iPhone HEIC/HEIF -> JPEG where the browser can decode it; otherwise the
  // original file (utils/heic-to-jpeg.ts).
  formData.append('file', await convertHeicToJpeg(file));

  return opsApiInstance.post(url, formData, {
    // Query parameters, not form fields: OPS knows them before the file.
    params: category ? { category, ...scope } : undefined,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// TODO: remove it when backend format all phones in one format
export const transformPhone = (phone: string | undefined) => {
  if (!phone) {
    return phone;
  }
  if (phone.startsWith('1')) {
    return '+' + phone;
  }
  if (!phone.startsWith('+1')) {
    return '+1' + phone;
  }

  return phone;
};

export const fillObjectByPath = (
  values: Record<string, any>,
  value: any,
  path: string
) => {
  const names = path.split('.');
  const dir = { ...values };

  names.reduce<Record<string, any>>((nextEnt, key, idx) => {
    if (!nextEnt[key]) {
      nextEnt[key] = {};
    }
    if (idx === names.length - 1) {
      nextEnt[key] = value;
    }
    return nextEnt[key];
  }, dir);

  return dir;
};

export const getDeepCopy = <T>(data: T) => {
  if (!data) {
    return data;
  }
  return structuredClone
    ? structuredClone(data)
    : (JSON.parse(JSON.stringify(data)) as T);
};

export const mergeObjects = (
  enteringObj1: Record<string, any>,
  enteringObj2: Record<string, any>
) => {
  const obj1 = getDeepCopy(enteringObj1);
  const obj2 = getDeepCopy(enteringObj2);

  const isValid = (val: any) => {
    return typeof val === 'object' && !Array.isArray(val) && val !== null;
  };
  const result = { ...obj1 };

  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key)) {
      if (isValid(obj2[key])) {
        if (isValid(result[key])) {
          result[key] = mergeObjects(result[key], obj2[key]);
        } else {
          result[key] = { ...obj2[key] };
        }
      } else {
        result[key] = obj2[key];
      }
    }
  }

  return result;
};

export const generateTemplate = (count: number, value = 'minmax(50px, 1fr)') =>
  Array.from({ length: count })
    .map(() => value)
    .join(' ');

export const addPercentageLabel = (value: string | number | undefined) =>
  `${value}%`;

export const makeSelectOptionsFromEntities = <T extends BaseEntity>(
  data: T[] | undefined
): SelectOption[] =>
  data?.map(({ id, name }) => ({
    value: id,
    label: name,
  })) ?? [];

export const makeSelectOptionsFromOptionsArray = (
  options: readonly string[],
  labelsObject?: Record<string, string>
): { label: string; value: string | number }[] =>
  options.map((value) => ({
    value,
    label: labelsObject ? labelsObject[value] : value,
  }));

export const joinRoutes = (
  routes: string[],
  params?: Record<string, string | number>
) => {
  const route = replaceAll(joinStrings(routes, '/'), '//', '/');
  if (!params) {
    return route;
  }
  return generatePath(route, params as Record<string, string>);
};

export const extractFileNameAndType = (url?: string) => {
  if (!url) {
    return { name: '', type: '' };
  }

  const fullFileName = url.split('/').pop();
  if (!fullFileName) {
    return { name: '', type: '' };
  }

  const [fileNameWithType] = fullFileName
    .split('-')
    .slice(1)
    .join('-')
    .split('.');
  const type = fullFileName.split('.').pop();

  return {
    type: type || '',
    name: fileNameWithType,
  };
};

export const getUploadedFile = (file?: string) => {
  if (file) {
    const { name, type } = extractFileNameAndType(file);
    const isImage = type === 'png' || type === 'jpg' || type === 'jpeg';
    return {
      src: file,
      file: { name, type: isImage ? FileType.Image : FileType.File } as File,
    };
  }
};

export const removeDefaultPropagation = (
  e: React.MouseEvent<
    HTMLDivElement | HTMLLabelElement | HTMLButtonElement | HTMLAnchorElement,
    MouseEvent
  >,
  withoutPrevent?: boolean
) => {
  if (!withoutPrevent) {
    e.preventDefault();
  }
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
};

export const handleFormSubmitWithFiles =
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  <Values extends FormikValues, Data extends any>({
      data,
      files,
      onSubmit,
      handleError,
      fieldsToRemove = [],
    }: {
      data: Data;
      fieldsToRemove?: (keyof Values)[];
      onSubmit: FormikConfig<Values>['onSubmit'];
      handleError: ReturnType<typeof useAlert>['handleError'];
      files: {
        dataFieldName?: string;
        requestFieldName: string;
        category?: UploadCategory;
        formFieldName: keyof Values;
        scope?: Record<string, string | number>;
      }[];
    }): FormikConfig<Values>['onSubmit'] =>
    async (values, formikHelpers) => {
      const upload = async (
        file?: UploadFile,
        uploadedUrl?: string,
        category?: UploadCategory,
        scope?: Record<string, string | number>
      ) => {
        if (!file) {
          return;
        }

        if (file.src === uploadedUrl) {
          return uploadedUrl;
        }

        const fileResponse = await uploadMultimedia<{
          data: { url: string };
        }>(file.file, URL.UPLOAD_FILE, category, scope);

        return fileResponse.data.url;
      };

      try {
        const uploadedFileUrls = Object.fromEntries(
          await Promise.all(
            files.map(
              async ({
                scope,
                category,
                dataFieldName,
                formFieldName,
                requestFieldName,
              }) => {
                const uploadedUrl = dataFieldName
                  ? getByPath(data, dataFieldName)
                  : (data as any)?.[requestFieldName];

                const uploadedUrlFinal = await upload(
                  values[formFieldName] as UploadFile,
                  uploadedUrl,
                  category,
                  scope
                );
                if (formFieldName !== requestFieldName) {
                  fieldsToRemove.push(formFieldName);
                }
                return [requestFieldName, uploadedUrlFinal];
              }
            )
          )
        );

        const formData = { ...values, ...uploadedFileUrls };

        if (fieldsToRemove?.length) {
          fieldsToRemove.forEach((field) => {
            delete formData[field];
          });
        }

        onSubmit(formData, formikHelpers);
      } catch (e) {
        handleError(e);
      }
    };

export const normalizeCardNumber: InputFieldProps['normalize'] = (value) => {
  const limitedValue = value.slice(0, 16);

  const parts = [];

  for (let i = 0; i < limitedValue.length; i += 4) {
    parts.push(limitedValue.slice(i, i + 4));
  }

  return parts.length > 1 ? parts.join(' ') : limitedValue;
};

const calculateTotalAmount = (items?: CartItem[]): number =>
  Number(
    items
      ?.reduce((acc, item) => acc + item?.price * item?.quantity, 0)
      .toFixed(2)
  );

const calculateTax = (totalAmount: number, taxRate: number) =>
  Number((totalAmount * (taxRate / 100)).toFixed(2));

export const calculateProcessingFees = (
  creditAmounts: number,
  processingFeesRate: number
): number => {
  return Number((creditAmounts * (processingFeesRate / 100)).toFixed(2));
};

const calculateCreditAmounts = (paymentMethods?: PaymentMethod[]) => {
  const creditInitialValue = { creditAmounts: 0, creditProccessingFees: 0 };

  return (
    paymentMethods?.reduce((acc, item) => {
      if (item.type === PaymentMethodType.Credit) {
        return {
          creditAmounts: acc.creditAmounts + item.amount,
          creditProccessingFees:
            acc.creditProccessingFees + +(item.proccessingFee ?? 0),
        };
      }
      return acc;
    }, creditInitialValue) || creditInitialValue
  );
};

export const isCartOrCheckout = (data?: Cart | EventCheckout): data is Cart => {
  return (data as Cart)?.shipping !== undefined;
};

export const getCartAmounts = ({
  data,
  tip = 0,
  paymentMethods,
  shippingPrice = 0,
  isProcessingFeeEnabled,
}: {
  tip?: number;
  shippingPrice?: number;
  data?: Cart | EventCheckout;
  isProcessingFeeEnabled?: boolean;
  paymentMethods?: PaymentMethod[];
}) => {
  const eventTax = data?.event?.tax ?? 0;
  const isFreeShipping = isCartOrCheckout(data)
    ? data?.shipping?.isFreeShipping
    : data?.isFreeShipping;

  const totalAmount = calculateTotalAmount(data?.items || []);
  const tax = calculateTax(totalAmount, eventTax);
  const netAmount = totalAmount + tax;

  const { creditAmounts, creditProccessingFees } =
    calculateCreditAmounts(paymentMethods);

  const processingFees = isProcessingFeeEnabled ? creditProccessingFees : 0;

  const shippingAmount = isFreeShipping ? 0 : +shippingPrice;

  const saleTotal = Number(
    (totalAmount + tax + tip + shippingAmount + processingFees).toFixed(2)
  );

  return {
    tax,
    netAmount,
    saleTotal,
    totalAmount,
    creditAmounts,
    processingFees,
    creditProccessingFees: isProcessingFeeEnabled ? creditProccessingFees : 0,
  };
};

export const calculateCheckoutAmounts = (data?: EventCheckout) => {
  const eventTax = data?.tax ?? 0;
  const totalAmount = calculateTotalAmount(data?.items);
  const { creditAmounts } = calculateCreditAmounts(data?.paymentMethods);

  const processingFees = calculateProcessingFees(
    creditAmounts ? creditAmounts - (data?.tip ?? 0) : 0,
    data?.processingFees || 0
  );
  const tax = calculateTax(totalAmount, eventTax);
  const netAmount = totalAmount;
  const grossAmount = totalAmount + tax;

  const saleTotal = Number(
    (
      grossAmount +
      (data?.tip ?? 0) +
      (data?.shippingPrice ? Number(data.shippingPrice) : 0) +
      processingFees
    ).toFixed(2)
  );

  return {
    tax,
    netAmount,
    saleTotal,
    totalAmount,
    grossAmount,
    processingFees,
  };
};

export const downloadFile = ({
  data,
  type,
  fileName,
}: {
  data: any;
  fileName: string;
  type: BlobPropertyBag['type'];
}) => {
  let url: string | undefined;
  let link: HTMLAnchorElement | undefined;
  try {
    if (
      data === undefined ||
      data === null ||
      data === '' ||
      (data instanceof Blob && data.size === 0)
    ) {
      toast.info('No data to export');
      return;
    }
    const blob = new Blob([data], { type });
    if (!blob.size) {
      toast.info('No data to export');
      return;
    }
    url = window.URL.createObjectURL(blob);
    link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    toast.success('Download started');
  } catch (error) {
    console.error(error);
    toast.error('Unable to download file');
  } finally {
    link?.remove();
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  }
};

export const getPluralSalesRep = (data: EventCheckout) => ({
  value: data?.commissions?.map((value) => value.user?.name)?.join(', '),
  title: getPlural('Sales rep', data.commissions.length, {
    onlyWord: true,
  }),
});

export const getTotalAmount = <T extends Record<string, any>>(
  field: keyof T,
  data?: T[],
  isFormatted: boolean = true
) => {
  const totalAmount = data?.reduce((acc, item) => acc + +item[field], 0);
  if (isFormatted) {
    return formatPrice(totalAmount);
  }
  return totalAmount;
};

/**
 * Calculates and determines the necessity of showing the price approval dialog based on cart items and company settings.
 *
 * @param {object} params - An object containing the input parameters.
 * @param {Cart | undefined} params.cartData - The cart data, including items with their prices, quantities, and associated product information.
 * @param {Company | undefined} params.company - The company data, which includes the setting for enabling price approval requests.
 * @param {boolean} params.showApprovalDialog - A boolean indicating whether the approval dialog is already being shown. This is used to prevent redundant displays.
 * @returns {object} An object containing the results of the calculation:
 * - `isShowApprovalDialog`: A boolean indicating whether the price approval dialog should be shown.
 * - `totalMinPriceProducts`: The total sum of the minimum prices of all products in the cart.
 * - `totalCartPriceProducts`: The total sum of the current prices of all products in the cart.
 * - `itemsUnderMinCount`: The number of items in the cart whose total price (price * quantity) is below their minimum price.
 * - `isPriceLowerThenMinPrice`: A boolean indicating whether at least one item's total price is lower than its minimum price.
 */
export const calculateProductsPriceForApprovalDialog = ({
  company,
  cartData,
  showApprovalDialog,
}: {
  cartData?: Cart;
  company?: Company;
  showApprovalDialog: boolean;
}) => {
  const initialValues = { totalMinPriceProducts: 0, totalCartPriceProducts: 0 };
  const items = cartData?.items ?? [];

  // Calculate overall totals for display
  const { totalMinPriceProducts, totalCartPriceProducts } =
    items.reduce((acc, value) => {
      const price = Number(value.price);
      const minPrice = Number(value.product?.minPrice ?? 0);

      acc.totalCartPriceProducts = Number(
        (acc.totalCartPriceProducts + price * value.quantity).toFixed(2)
      );
      acc.totalMinPriceProducts = Number(
        (acc.totalMinPriceProducts + minPrice * value.quantity).toFixed(2)
      );

      return acc;
    }, initialValues) ?? initialValues;

  const isExchange = items.some((value) => value.isExchange);

  // Identify items whose individual price is below their minPrice threshold
  const isUnderMinimum = totalCartPriceProducts < totalMinPriceProducts;

  // Check if any of those under-min items still lack approval
  const hasUnapprovedUnderMinItems = isUnderMinimum
    ? items.some((item) => !Boolean(item.priceApprovalId || item.priceApproval))
    : false;

  // Show dialog when at least one item is under min and not yet approved
  const isShowApprovalDialog =
    !isExchange &&
    !showApprovalDialog &&
    isUnderMinimum &&
    hasUnapprovedUnderMinItems &&
    company?.isApprovalRequestEnabled;

  return {
    isShowApprovalDialog,
    totalMinPriceProducts,
    totalCartPriceProducts,
    isPriceLowerThenMinPrice: isUnderMinimum,
    itemsUnderMin: isUnderMinimum ? items : [],
  };
};

export const getCommissionsAmount = (checkout: EventCheckout) =>
  checkout?.commissions?.reduce(
    (acc, commission) => acc + (Number(commission.cash) || 0),
    0
  );

export const carrierOptions = Object.values(Carrier).map((value) => ({
  value,
  label: CarrierTitle[value],
}));

export const isEventManager = (user?: User): boolean =>
  user?.role?.name
    ? managerRoles.includes(user.role.name as BackOfficeRoles)
    : false;

export const getTipEmployeePercentage = ({
  tip,
  userId,
  commissions,
}: {
  tip: number;
  userId: number;
  commissions?: Commission[];
}) => {
  const userCommission = commissions?.find(
    (commission) => commission.userId === userId
  );

  if (!userCommission) {
    return 0;
  }

  const tipAmount = (tip / 100) * userCommission.amount;

  return Number(tipAmount.toFixed(2));
};

export const isShippedEventOrder = ({ eventOrder }: EventCheckout) => {
  if (!eventOrder) {
    return false;
  }

  return (
    !!eventOrder?.trackingUrl ||
    !!eventOrder?.trackingNumber ||
    eventOrder?.status === EventOrderStatus.SHIPPED
  );
};

// Owner 17 Oct 2026 (Annapolis): issuers print placeholder names for many
// different people ("VISA CARDHOLDER", "A GIFT FOR YOU", "VISA" plus a product
// word). Same rule as clix-shared isGenericCardholderName.
const placeholderWords = new Set([
  'CARDHOLDER',
  'CARDMEMBER',
  'PREPAID',
  'VALUED',
  'CUSTOMER',
  'CONTACTLESS',
  'VANILLA',
  'INSTANT',
  'VISA',
  'MASTERCARD',
  'AMEX',
  'DISCOVER',
]);
const placeholderFillerWords = new Set([
  'CARD',
  'HOLDER',
  'MEMBER',
  'GIFT',
  'DEBIT',
  'CREDIT',
  'ISSUE',
  'BUSINESS',
  'CORPORATE',
  'COMMERCIAL',
  'FNB',
  'MC',
  'AMERICAN',
  'EXPRESS',
  'A',
  'FOR',
  'YOU',
  'THE',
  'OF',
  'MY',
]);

export const isGenericCardholderName = (name?: string | null) => {
  if (!name) {
    return true;
  }

  const normalizedName = name.trim().toUpperCase();
  if (
    genericCardholderNames.some((genericName) => normalizedName === genericName)
  ) {
    return true;
  }
  const words = normalizedName.split(/[^A-Z0-9']+/).filter(Boolean);
  return (
    !words.length ||
    words.some((word) => placeholderWords.has(word)) ||
    words.every((word) => placeholderFillerWords.has(word))
  );
};

export const formatCaseInfo = <T>(
  data: T | undefined,
  config: FormatCaseInfo<T>[]
) => {
  if (!data) {
    return [];
  }

  return config.map(
    ({ key, title, format, getValue, fallback = EmptyPlaceholder }) => {
      const rawValue = getValue ? getValue(data) : key ? data[key] : undefined;

      const value =
        rawValue != null
          ? format
            ? format(rawValue as string | number)
            : rawValue
          : fallback;

      return { title, value };
    }
  );
};

export const getChargebackDescription = (
  data?: FiservCaseDetail
): string | null => {
  if (!data) {
    return null;
  }

  return CHARGEBACK_STATUS_MAP[data.CBType]?.[data.Disposition || ''] ?? null;
};

export const getLatestChargebackDescription = (data?: Chargeback) => {
  const history = data?.fiservChargeback?.rawDataHistory;
  const lastFiservChargeback = history && history?.[history?.length - 1];

  return getChargebackDescription(lastFiservChargeback);
};
